import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { collection, getDocs, doc, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import toast from 'react-hot-toast';


const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filter, setFilter] = useState('all'); // all, unread, read

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const messagesRef = collection(db, 'messages');
      const q = query(messagesRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const messagesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(messagesData);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await updateDoc(doc(db, 'messages', messageId), {
        status: 'read'
      });
      setMessages(messages.map(msg =>
        msg.id === messageId ? { ...msg, status: 'read' } : msg
      ));
      toast.success('Marked as read');
    } catch (error) {
      console.error('Error updating message:', error);
      toast.error('Failed to update message');
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      await deleteDoc(doc(db, 'messages', messageId));
      setMessages(messages.filter(msg => msg.id !== messageId));
      setSelectedMessage(null);
      toast.success('Message deleted');
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Failed to delete message');
    }
  };

  const filteredMessages = messages.filter(msg => {
    if (filter === 'all') return true;
    return msg.status === filter;
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSubjectIcon = (subject) => {
    const icons = {
      general: '💬',
      order: '📦',
      product: '👗',
      collaboration: '🤝',
      feedback: '⭐'
    };
    return icons[subject] || '💬';
  };

  const unreadCount = messages.filter(msg => msg.status === 'unread').length;

  return (
    <div className="pb-8 md:pb-16">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 md:mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-serif text-deep-brown">Messages</h1>
            <p className="text-sm md:text-base text-warm-gray font-medium">Manage customer messages</p>
          </div>
        </div>



        {/* Main Content */}
        <div className="mt-8">
          {/* Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-serif font-light text-deep-brown mb-2">
                  Messages
                </h1>
                <p className="text-sm md:text-base text-warm-gray">
                  {unreadCount > 0 && (
                    <span className="text-deep-brown font-medium">
                      {unreadCount} unread message{unreadCount !== 1 ? 's' : ''}
                    </span>
                  )}
                  {unreadCount === 0 && 'All messages read'}
                </p>
              </div>

              {/* Filter Buttons */}
              <div className="flex flex-wrap gap-2">
                {['all', 'unread', 'read'].map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-colors duration-200 ${filter === f
                        ? 'bg-deep-brown text-white'
                        : 'bg-white text-deep-brown hover:bg-deep-brown/10'
                      }`}
                  >
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-deep-brown mx-auto mb-4"></div>
                <p className="text-warm-gray">Loading messages...</p>
              </div>
            </div>
          ) : filteredMessages.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <div className="text-6xl mb-4">📭</div>
              <p className="text-xl text-warm-gray">No messages found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
              {/* Messages List */}
              <div className="lg:col-span-1 space-y-2 md:space-y-3 max-h-[calc(100vh-300px)] overflow-y-auto pr-2">
                {filteredMessages.map((message) => (
                  <motion.div
                    key={message.id}
                    className={`bg-white rounded-lg p-3 md:p-4 cursor-pointer transition-all duration-200 border-2 ${selectedMessage?.id === message.id
                        ? 'border-deep-brown shadow-lg'
                        : 'border-transparent hover:border-warm-gray/30 hover:shadow-md'
                      }`}
                    onClick={() => setSelectedMessage(message)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-xl md:text-2xl">{getSubjectIcon(message.subject)}</span>
                        {message.status === 'unread' && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        )}
                      </div>
                      <span className="text-xs text-warm-gray">
                        {formatDate(message.createdAt)}
                      </span>
                    </div>
                    <h3 className="font-medium text-deep-brown mb-1 text-sm md:text-base">
                      {message.firstName} {message.lastName}
                    </h3>
                    <p className="text-xs md:text-sm text-warm-gray mb-2 truncate">{message.email}</p>
                    <p className="text-xs md:text-sm text-deep-brown capitalize">
                      {message.subject?.replace('-', ' ')}
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* Message Detail */}
              <div className="lg:col-span-2">
                {selectedMessage ? (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-lg shadow-lg p-4 md:p-6"
                  >
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row items-start justify-between gap-4 mb-4 md:mb-6 pb-4 md:pb-6 border-b">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 md:space-x-3 mb-3">
                          <span className="text-3xl md:text-4xl">{getSubjectIcon(selectedMessage.subject)}</span>
                          <div>
                            <h2 className="text-lg md:text-xl lg:text-2xl font-serif font-light text-deep-brown">
                              {selectedMessage.firstName} {selectedMessage.lastName}
                            </h2>
                            <p className="text-sm md:text-base text-warm-gray truncate">{selectedMessage.email}</p>
                          </div>
                        </div>
                        {selectedMessage.phone && (
                          <p className="text-sm md:text-base text-warm-gray">
                            <span className="font-medium">Phone:</span> {selectedMessage.phone}
                          </p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex flex-wrap items-center gap-2">
                        {selectedMessage.status === 'unread' && (
                          <motion.button
                            onClick={() => markAsRead(selectedMessage.id)}
                            className="px-3 md:px-4 py-1.5 md:py-2 bg-blue-500 text-white text-xs md:text-sm rounded-lg hover:bg-blue-600 transition-colors duration-200"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            Mark as Read
                          </motion.button>
                        )}
                        <motion.button
                          onClick={() => deleteMessage(selectedMessage.id)}
                          className="px-3 md:px-4 py-1.5 md:py-2 bg-red-500 text-white text-xs md:text-sm rounded-lg hover:bg-red-600 transition-colors duration-200"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          Delete
                        </motion.button>
                      </div>
                    </div>

                    {/* Subject */}
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-warm-gray mb-2">Subject</h3>
                      <p className="text-lg text-deep-brown capitalize">
                        {selectedMessage.subject?.replace('-', ' ')}
                      </p>
                    </div>

                    {/* Message */}
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-warm-gray mb-2">Message</h3>
                      <div className="bg-cream/50 rounded-lg p-4">
                        <p className="text-deep-brown whitespace-pre-wrap leading-relaxed">
                          {selectedMessage.message}
                        </p>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="pt-6 border-t">
                      <p className="text-sm text-warm-gray">
                        Received on {formatDate(selectedMessage.createdAt)}
                      </p>
                      {selectedMessage.status === 'read' && (
                        <p className="text-sm text-green-600 mt-1">
                          ✓ Marked as read
                        </p>
                      )}
                    </div>

                    {/* Quick Actions */}
                    <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t">
                      <h3 className="text-xs md:text-sm font-medium text-warm-gray mb-3">Quick Actions</h3>
                      <div className="flex flex-wrap gap-2 md:gap-3">
                        <a
                          href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                          className="px-3 md:px-4 py-1.5 md:py-2 bg-deep-brown text-white text-xs md:text-sm rounded-lg hover:bg-warm-gray transition-colors duration-200"
                        >
                          Reply via Email
                        </a>
                        {selectedMessage.phone && (
                          <a
                            href={`tel:${selectedMessage.phone}`}
                            className="px-3 md:px-4 py-1.5 md:py-2 bg-green-600 text-white text-xs md:text-sm rounded-lg hover:bg-green-700 transition-colors duration-200"
                          >
                            Call
                          </a>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                    <div className="text-6xl mb-4">👈</div>
                    <p className="text-xl text-warm-gray">
                      Select a message to view details
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMessages;
